import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputAdornment, TextFieldProps } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import cx from 'classnames';
import { isEmpty, omit } from 'lodash';
import { Moment } from 'moment';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { TextField } from '..';
import { MomentFormats } from '../../../../../common/utils/date';
import { withThemeNext } from '../../../../layout/theme-next';
import { useInputStyles, useStyles } from './Datepicker.styles';
import { DatePickerProps } from './Datepicker.types';

const PROPS_TO_SKIP: Array<keyof TextFieldProps> = [
  'onKeyUp',
  'variant',
  'contentEditable',
  'draggable',
  'spellCheck',
  'margin',
  'helperText',
];

type CustomTextFieldProps = TextFieldProps & {
  onClear?: () => void
  onCalendarClick?: () => void
  size?: 'small' | 'medium'
};

const DatepickerTextField: React.FC<CustomTextFieldProps> = ({
  onKeyDown,
  onClear,
  onCalendarClick,
  disabled,
  inputProps,
  size = 'small',
  ...props
}) => {
  const classes = useInputStyles();
  const suitableProps = omit(props, ...PROPS_TO_SKIP);
  const isClearVisible = onClear && !isEmpty(props.value);

  return (
    <TextField
      {...suitableProps}
      inputProps={inputProps}
      disabled={disabled}
      size={size}
      onClick={() => {
        if (!disabled) {
          onCalendarClick?.();
        }
      }}
      onKeyDown={(e) =>
        onKeyDown?.(e as React.KeyboardEvent<HTMLDivElement>)
      }
      endAdornment={
        <InputAdornment
          position="end"
          className={cx({
            [classes.endAdorment]: !disabled,
            [classes.endAdormentDisabled]: disabled,
          })}
        >
          {isClearVisible && (
            <FontAwesomeIcon
              className={classes.clear}
              data-cy="date-clear"
              icon={regular('xmark')}
              onClick={(e) => {
                e.stopPropagation();
                onClear?.();
              }}
            />
          )}
          <FontAwesomeIcon
            data-cy="calendar-day"
            icon={regular('calendar-day')}
            onClick={(e) => {
              e.stopPropagation();
              onCalendarClick?.();
            }}
          />
        </InputAdornment>
      }
    />
  );
};

const Datepicker: React.FC<DatePickerProps> = ({
  format = MomentFormats.MonthDateYearTwoDigit,
  onClear,
  size,
  ...props
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      adapterLocale={intl.locale}
    >
      <DatePicker<Moment>
        {...props}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        inputFormat={format}
        PopperProps={{
          className: classes.popover,
        }}
        renderInput={(params) => (
          <DatepickerTextField
            {...params}
            size={size}
            onClear={onClear}
            onCalendarClick={() => setOpen(true)}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default withThemeNext(Datepicker);
